import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, GripVertical } from "lucide-react";
import ActionConfigEditor from "@/components/pages/workflows/ActionConfigEditor";
import { actionOptionsFor, AUTO_OUTREACH_DESCRIPTIONS } from "@/components/pages/workflows/WorkflowConstants";
import type { WorkflowActionConfigValue, WorkflowActionType, WorkflowTriggerType } from "@/model/interface/Workflow";

export interface ActionFormData {
  id?: string;
  actionType: WorkflowActionType;
  config: Record<string, WorkflowActionConfigValue>;
  delayMinutes: number;
}

interface ActionCardProps {
  action: ActionFormData;
  index: number;
  triggerType: WorkflowTriggerType;
  autoOutreachDisabled: boolean;
  onUpdate: (updates: Partial<ActionFormData>) => void;
  onUpdateConfig: (key: string, value: WorkflowActionConfigValue) => void;
  onRemove: () => void;
}

const ActionCard: React.FC<ActionCardProps> = ({
  action,
  index,
  triggerType,
  autoOutreachDisabled,
  onUpdate,
  onUpdateConfig,
  onRemove,
}) => {
  return (
    <Card>
      <CardContent className="pt-4 pb-3 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <GripVertical className="h-4 w-4" />
            Step {index + 1}
          </div>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onRemove}>
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Action Type</Label>
            <Select
              value={action.actionType}
              onValueChange={(value) => onUpdate({ actionType: value as WorkflowActionType, config: {} })}
            >
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {actionOptionsFor(triggerType).map((option) => {
                  const outreachDescription = AUTO_OUTREACH_DESCRIPTIONS[option.value];
                  const blocked = autoOutreachDisabled && outreachDescription != null;
                  return (
                    <SelectItem key={option.value} value={option.value} disabled={blocked}>
                      {option.label}
                      {blocked && (
                        <span className="block text-xs text-muted-foreground">
                          {outreachDescription} · enable auto-outreach in Organisation Settings
                        </span>
                      )}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Delay (minutes)</Label>
            <Input
              type="number"
              min={0}
              value={action.delayMinutes}
              onChange={(event) => onUpdate({ delayMinutes: Number(event.target.value) || 0 })}
              className="mt-1"
            />
          </div>
        </div>

        <ActionConfigEditor
          actionType={action.actionType}
          config={action.config}
          onConfigChange={onUpdateConfig}
        />
      </CardContent>
    </Card>
  );
};

export default ActionCard;
